import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, Play } from "lucide-react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["all", "artistes", "musiciens", "scientifiques"];
const filterTestIds = {
  all: "artist-filter-all",
  artistes: "artist-filter-artists",
  musiciens: "artist-filter-musicians",
  scientifiques: "artist-filter-scientists",
};

const PersonModal = ({ person, onClose }) => {
  const { lang, t } = useLang();
  useEffect(() => {
    window.__lenis?.stop();
    return () => window.__lenis?.start();
  }, []);

  return (
    <motion.div
      data-testid="artist-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/85 p-4 backdrop-blur-md sm:p-8"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.97 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative grid max-h-[88vh] w-full max-w-4xl grid-cols-1 overflow-y-auto border border-gold/25 bg-ink2 md:grid-cols-2"
      >
        <button
          data-testid="artist-modal-close-button"
          aria-label={t.people.close}
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full border border-gold/40 bg-ink/70 p-2 text-parchment backdrop-blur-md transition-colors hover:bg-gold hover:text-ink"
        >
          <X size={18} />
        </button>
        <div className="relative min-h-[320px] md:min-h-full">
          <img src={person.image} alt={person.name} className="absolute inset-0 h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink2/80 via-transparent to-transparent md:bg-gradient-to-r" />
        </div>
        <div className="p-8 sm:p-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
            {lang === "fr" ? person.discipline_fr : person.discipline_i18n?.[lang] || person.discipline_en || person.discipline_fr}
          </p>
          <h3 className="mt-3 font-serif text-3xl font-light text-parchment sm:text-4xl">{person.name}</h3>
          <div className="mt-5 h-px w-16 bg-gold/50" />
          <p className="mt-6 whitespace-pre-line text-sm font-light leading-relaxed text-sand sm:text-base">
            {(lang === "fr" ? person.bio_fr : person.bio_i18n?.[lang] || person.bio_en || person.bio_fr) || ""}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {person.url && (
              <a
                data-testid="artist-modal-website-link"
                href={person.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink"
              >
                <ExternalLink size={13} /> {t.people.visit}
              </a>
            )}
            {person.video_url && (
              <a
                data-testid="artist-modal-video-link"
                href={person.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full border border-parchment/30 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-parchment transition-colors hover:border-gold hover:text-gold"
              >
                <Play size={13} /> {t.people.video}
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const People = () => {
  const { lang, t } = useLang();
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/artists`)
      .then((res) => setPeople(res.data.filter((p) => p.category !== "bureau")))
      .catch(() => setPeople([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? people : people.filter((p) => p.category === filter)),
    [people, filter]
  );

  return (
    <section id="residents" data-testid="people-section" className="mx-auto max-w-[1600px] px-4 py-24 sm:px-8 lg:px-16 lg:py-36">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{t.people.kicker}</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 font-serif text-3xl font-light text-parchment sm:text-4xl lg:text-5xl">
          {t.people.title}
        </h2>
      </Reveal>
      <Reveal delay={0.18}>
        <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-sand sm:text-lg">{t.people.sub}</p>
      </Reveal>

      <Reveal delay={0.24}>
        <div className="mt-10 flex flex-wrap gap-3">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={filterTestIds[f]}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] transition-all duration-300 ${
                filter === f
                  ? "border-gold bg-gold text-ink"
                  : "border-gold/25 text-sand hover:border-gold/60 hover:text-parchment"
              }`}
            >
              {t.people.filters[f]}
            </button>
          ))}
        </div>
      </Reveal>

      {loading ? (
        <p data-testid="people-loading" className="mt-16 font-mono text-xs uppercase tracking-[0.3em] text-sand">
          {t.people.loading}
        </p>
      ) : (
        <motion.div layout className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((person) => (
              <motion.button
                layout
                key={person.id}
                data-testid="artist-card-view-profile-button"
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => setSelected(person)}
                className="group relative block overflow-hidden border border-gold/10 bg-card2 text-left transition-colors duration-500 hover:border-gold/50"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={person.image}
                    alt={person.name}
                    loading="lazy"
                    className="h-full w-full object-cover object-top grayscale-[45%] transition-all duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
                  <span className="absolute right-4 top-4 font-mono text-[9px] uppercase tracking-[0.25em] text-gold/0 transition-colors duration-500 group-hover:text-gold">
                    {t.people.view} →
                  </span>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold/90">
                    {lang === "fr" ? person.discipline_fr : person.discipline_i18n?.[lang] || person.discipline_en || person.discipline_fr}
                  </p>
                  <h3 className="mt-1.5 font-serif text-2xl font-light leading-tight text-parchment">
                    {person.name}
                  </h3>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      <AnimatePresence>
        {selected && <PersonModal person={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
};

export default People;
